import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, TablePagination, IconButton, Menu, MenuItem } from '@mui/material';
import './RecentlyDeleted.css';
import { RestoreFromTrash as RestoreIcon, DeleteForever as DeleteIcon, FilterList as FilterIcon } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { poolMembersService } from '../../services';

const RecentlyDeletedPool = () => {
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const [isLoading, setIsLoading] = useState(true);
  const [feesFilter, setFeesFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = (filterValue) => {
    if (filterValue) {
      setFeesFilter(filterValue);
    }
    setAnchorEl(null);
  };

  useEffect(() => {
    const fetchMembers = async () => {
      setIsLoading(true);
      try {
        const data = await poolMembersService.getRecentlyDeletedSwimmers();
        console.log('Fetched deleted pool members:', data);
        setMembers(data);
        
        data.forEach(member => {
          if (member.FeesStatus?.toLowerCase() === 'pending') {
            toast.warning(`${member.Name}'s fees is pending!`, {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          }
        });
        
      } catch (error) {
        console.error('Error fetching deleted pool members:', error);
        toast.error('Failed to fetch deleted pool members data!');
      } finally {
        setIsLoading(false);
      }
    };
    fetchMembers();
  }, []);

  const handleRestore = async (userId) => {
    try {
      await poolMembersService.restoreSwimmer(userId);
      toast.success('Pool member restored successfully!');
      // Remove restored user from local state
      setMembers(members.filter(member => member._id !== userId));
    } catch (error) {
      console.error('Error restoring pool member:', error);
      toast.error('Error restoring pool member');
    }
  };

  const handlePermanentDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this pool member? This action cannot be undone.')) return;
    setDeletingId(userId);
    try {
      await poolMembersService.permanentlyDeleteSwimmer(userId);
      toast.success('Pool member permanently deleted!');
      setMembers(members.filter(member => member._id !== userId));
    } catch (error) {
      toast.error('Error deleting pool member');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedMembers = [...members].sort((a, b) => {
    if (a.FeesStatus?.toLowerCase() === 'pending' && b.FeesStatus?.toLowerCase() !== 'pending') return -1;
    if (a.FeesStatus?.toLowerCase() !== 'pending' && b.FeesStatus?.toLowerCase() === 'pending') return 1;
    return 0;
  });

  const filteredMembers = sortedMembers.filter(member => {
    const name = member.Name?.toLowerCase() || '';
    const address = member.Address?.toLowerCase() || '';
    const mobile = member.Mobile?.toString().toLowerCase() || '';
    const occupation = member.Occupation?.toLowerCase() || '';
    const age = member.Age?.toString() || '';
    const dueDate = member.Duedate?.toString() || '';
    const startDate = member.Startdate?.toString() || '';
    const feesStatus = member.FeesStatus?.toLowerCase() || '';
    const pendingFees = member.PendingFees?.toString() || '';

    const searchMatches = (
      name.includes(searchTerm.toLowerCase()) ||
      address.includes(searchTerm.toLowerCase()) ||
      mobile.includes(searchTerm.toLowerCase()) ||
      occupation.includes(searchTerm.toLowerCase()) ||
      age.includes(searchTerm) ||
      dueDate.includes(searchTerm) ||
      startDate.includes(searchTerm) ||
      feesStatus.includes(searchTerm.toLowerCase()) ||
      pendingFees.includes(searchTerm)
    );

    const feesMatches = 
      feesFilter === 'all' ? true :
      feesFilter === 'pending' ? member.FeesStatus?.toLowerCase() === 'pending' :
      feesFilter === 'clear' ? member.FeesStatus?.toLowerCase() === 'clear' : true;

    return searchMatches && feesMatches;
  });

  return (
    <>
      <ToastContainer />
      <div className="gym-members-container">
        {isLoading ? (
          <div className='login__loading' style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh'}}>
            <svg className="ip" viewBox="0 0 256 128" width="128px" height="64px" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="grad1" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#5ebd3e" />
                  <stop offset="33%" stopColor="#ffb900" />
                  <stop offset="67%" stopColor="#f78200" />
                  <stop offset="100%" stopColor="#e23838" />
                </linearGradient>
                <linearGradient id="grad2" x1="1" y1="0" x2="0" y2="0">
                  <stop offset="0%" stopColor="#e23838" />
                  <stop offset="33%" stopColor="#973999" />
                  <stop offset="67%" stopColor="#009cdf" />
                  <stop offset="100%" stopColor="#5ebd3e" />
                </linearGradient>
              </defs>
              <g fill="none" strokeLinecap="round" strokeWidth="16">
                <g className="ip__track" stroke="#ddd">
                  <path d="M8,64s0-56,60-56,60,112,120,112,60-56,60-56"/>
                  <path d="M248,64s0-56-60-56-60,112-120,112S8,64,8,64"/>
                </g>
                <g strokeDasharray="180 656">
                  <path className="ip__worm1" stroke="url(#grad1)" strokeDashoffset="0" d="M8,64s0-56,60-56,60,112,120,112,60-56,60-56"/>
                  <path className="ip__worm2" stroke="url(#grad2)" strokeDashoffset="358" d="M248,64s0-56-60-56-60,112-120,112S8,64,8,64"/>
                </g>
              </g>
            </svg>
          </div>
        ) : members.length === 0 ?(
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh',fontSize: '1.5rem',color:'#6d2057'}}>
            No Recently Deleted Pool Members
            </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
              <TextField
                className="search-field"
                label="Search members"
                variant="outlined"
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ flexGrow: 1}}
              />
              <IconButton onClick={handleFilterClick}>
                <FilterIcon style={{backgroundColor: "#6d2057", color:"white", padding: "10px", fontSize: "24px"}}/>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => handleFilterClose()}
              >
                <MenuItem onClick={() => handleFilterClose('all')}>All Members</MenuItem>
                <MenuItem onClick={() => handleFilterClose('pending')}>Pending Fees</MenuItem>
                <MenuItem onClick={() => handleFilterClose('clear')}>Paid Fees</MenuItem>
              </Menu>
            </div>

            <TableContainer component={Paper} className="table-container">
              <Table className="members-table">
              <TableHead className="table-head">
                <TableRow>
                  <TableCell className="table-head-cell">Image</TableCell>
                  <TableCell className="table-head-cell">Name</TableCell>
                  <TableCell className="table-head-cell">Address</TableCell>
                  <TableCell className="table-head-cell">Mobile</TableCell>
                  <TableCell className="table-head-cell">Occupation</TableCell>
                  <TableCell className="table-head-cell">Age</TableCell>
                  <TableCell className="table-head-cell">Deleted At</TableCell>
                  <TableCell className="table-head-cell">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMembers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((member) => (
                  <TableRow key={member._id} className="table-row">
                    <TableCell>
                      {member.Image ? (
                        <img
                          src={member.Image}
                          alt="Member"
                          className="member-image"
                        />
                      ) : (
                        <span>No Image</span>
                      )}
                    </TableCell>
                    <TableCell>{member.Name}</TableCell>
                    <TableCell>{member.Address}</TableCell>
                    <TableCell>{member.Mobile}</TableCell>
                    <TableCell>{member.Occupation}</TableCell>
                    <TableCell>{member.Age}</TableCell>
                    <TableCell>{new Date(member.deletedAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton 
                        onClick={() => handleRestore(member._id)}
                        style={{ color: '#6d2057' }}
                      >
                        <RestoreIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handlePermanentDelete(member._id)}
                        style={{ color: 'red' }}
                        disabled={deletingId === member._id}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={filteredMembers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[1000, 1500, 2000]}
          />
        </>
      )}
    </div>
  </>
);
};

export default RecentlyDeletedPool;
